/**
 * Forwards Content-Security-Policy violations to New Relic so the policy can be
 * observed across ordinary use of an environment rather than only where a
 * developer happens to have the console open.
 *
 * A blocked resource is not a JavaScript error, so New Relic does not capture
 * these on its own; the browser raises a securitypolicyviolation event and this
 * module ships it as a page action.
 */

const EXTENSION_SCHEMES = [
    "chrome-extension:",
    "moz-extension:",
    "safari-extension:",
    "safari-web-extension:"
];

/**
 * The browser event, widened for the fields TypeScript 3.7's DOM lib omits.
 * disposition distinguishes a report-only observation from a live block.
 */
type CspViolationEvent = SecurityPolicyViolationEvent & { disposition?: string };

interface ICspPageAction {
    violatedDirective: string;
    effectiveDirective: string;
    blockedURI: string;
    documentURI: string;
    sourceFile: string;
    lineNumber: number;
    disposition: string;
}

interface INewRelicApi {
    addPageAction?: (name: string, attributes: Record<string, unknown>) => void;
}

function hasExtensionScheme(value: string): boolean {
    const uri = (value || "").toLowerCase();
    return EXTENSION_SCHEMES.some(scheme => uri.startsWith(scheme));
}

/**
 * True for violations raised by a browser extension injecting into the page.
 * These are not application defects and must not count towards the report-only
 * exit condition.
 *
 * All three attribution fields are checked, not just blockedURI. An extension
 * that injects an inline script produces a blockedURI of "inline", identical to
 * one from the application's own bundle; only sourceFile distinguishes them. An
 * inline violation with no extension scheme anywhere is kept, because that is
 * how a genuine inline script in the application would present.
 */
function isExtensionNoise(event: CspViolationEvent): boolean {
    return hasExtensionScheme(event.blockedURI)
        || hasExtensionScheme(event.sourceFile)
        || hasExtensionScheme(event.documentURI);
}

function getNewRelic(): INewRelicApi | undefined {
    return (window as Window & { newrelic?: INewRelicApi }).newrelic;
}

function toPageAction(event: CspViolationEvent): ICspPageAction {
    return {
        violatedDirective: event.violatedDirective,
        effectiveDirective: event.effectiveDirective,
        blockedURI: event.blockedURI,
        documentURI: event.documentURI,
        sourceFile: event.sourceFile,
        lineNumber: event.lineNumber,
        disposition: event.disposition || "enforce"
    };
}

/** Sends one violation to New Relic, unless it is extension noise. */
export function reportCspViolation(event: CspViolationEvent): void {
    if (isExtensionNoise(event)) {
        return;
    }

    const newrelic = getNewRelic();
    if (!newrelic || typeof newrelic.addPageAction !== "function") {
        return;
    }

    newrelic.addPageAction("CspViolation", { ...toPageAction(event) });
}

/**
 * Drains violations collected before the application bundle was parsed.
 *
 * A blocked stylesheet in <head> raises its violation before any bundled code
 * runs, so the container's generated New Relic script buffers events into a
 * global array. That script holds no filtering logic of its own precisely so
 * this module stays the only place a violation is interpreted.
 */
function drainBufferedViolations(): void {
    const buffered = (window as Window & { __cspViolations?: CspViolationEvent[] }).__cspViolations;
    if (!Array.isArray(buffered)) {
        return;
    }

    while (buffered.length > 0) {
        const event = buffered.shift();
        if (event) {
            reportCspViolation(event);
        }
    }
}

/** Starts forwarding violations for the lifetime of the document. */
export function registerCspViolationReporter(): void {
    drainBufferedViolations();

    document.addEventListener("securitypolicyviolation", (event: Event) => {
        reportCspViolation(event as CspViolationEvent);
    });
}
